'use client'
import { ChevronDownIcon } from '@repo/studio-design-system-ui-icons/arrows/chevron-down'
import { Button } from '@repo/studio-design-system-ui-button'
import * as React from 'react'
import * as Popover from 'primitives/popover'
import { tw } from 'utils/classnames'
import { capitaliseFirstLetter } from 'utils/string'

import { useFilterList } from './hooks'
import type { Filter } from './constants'
import { Checkbox } from 'primitives/checkbox'

function getCollisionBoundary() {
  return typeof window === 'undefined' ? [] : [document.querySelector('#templates-page-content')]
}

interface FilterProps {
  filter: Filter
  filterContentClassName?: string
  shouldIgnorePortal?: boolean
}
export function Filter({
  filter,
  filterContentClassName,
  shouldIgnorePortal = false,
}: FilterProps) {
  const filterAttribute = filter.label
  const [open, setOpen] = React.useState(false)
  const { items, onValueChange } = useFilterList(filterAttribute)
  const numOfSelectedOptions = items.filter(item => item.isRefined).length

  if (items.length === 0) {
    return null
  }

  const PopoverWrapper = shouldIgnorePortal ? React.Fragment : Popover.Portal

  return (
    <Popover.Root id="filter-photos" open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <Button
          variant="tertiary"
          className={`relative border-gray-800 text-xs font-medium hover:border-gray-700 hover:bg-gray-800 hover:text-gray-100 ${
            numOfSelectedOptions > 0 ? 'text-violet-500' : 'text-gray-100'
          }`}
        >
          {capitaliseFirstLetter(filterAttribute)}{' '}
          <ChevronDownIcon className="ml-2 size-4 text-gray-500" />
          {numOfSelectedOptions > 0 ? (
            <div className="text-service-white-white text-10 absolute right-[-4px] top-[-4px] flex size-4 items-center justify-center rounded-full bg-violet-500">
              {numOfSelectedOptions}
            </div>
          ) : null}
        </Button>
      </Popover.Trigger>
      <PopoverWrapper>
        <Popover.Content
          className={tw(
            'z-30 flex max-h-[calc(var(--radix-popover-content-available-height)-30px)] max-w-[calc(var(--radix-popover-content-available-width)-30px)]',
            filterContentClassName,
          )}
          align="center"
          side="bottom"
          sideOffset={4}
          collisionBoundary={getCollisionBoundary()}
          collisionPadding={12}
        >
          <div
            className={tw(
              !open && 'hidden',
              'rounded-4 bg-gray-1000 grid max-h-[312px] gap-3 self-start overflow-auto border border-gray-800 p-4 shadow-[0px_10px_10px_0px_rgba(0,0,0,0.04)]',
              items.length <= 9
                ? '[grid-auto-flow:row] [grid-auto-rows:auto]'
                : 'grid-rows-9 [grid-auto-flow:column] [grid-auto-rows:auto]',
              'max-sm:grid-rows-auto max-sm:grid-cols-1 max-sm:[grid-auto-flow:row]',
            )}
          >
            {items.map(option => (
              <div className="flex items-center font-medium leading-5" key={option.label}>
                <Checkbox
                  className="bg-gray-1000 hover:border-gray-400-400 size-5 min-w-5 shrink-0 border-gray-700"
                  checked={option.isRefined}
                  onClick={e => e.stopPropagation()}
                  onCheckedChange={() =>
                    onValueChange(
                      filter.mapValue?.fromLabel
                        ? filter.mapValue.fromLabel(option.value)
                        : option.value,
                    )
                  }
                  id={option.value}
                />
                <label
                  htmlFor={option.value}
                  className="min-w-0 group-hover:text-gray-50 block max-w-[250px] truncate text-gray-300 max-sm:max-w-full"
                >
                  {filter.mapValue?.toLabel ? filter.mapValue.toLabel(option.label) : option.label}
                </label>
                <span className="text-gray-400-400 ml-1">({option.count})</span>
              </div>
            ))}
          </div>
        </Popover.Content>
      </PopoverWrapper>
    </Popover.Root>
  )
}
